import {
  PrimaryButton,
  SaladIcon,
  SecondaryButton,
  SnackbarStatus,
  Typography,
  useSnackbar,
} from '@weareopenr/component-library';
import { useLocation, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { createMealtimeCategories } from 'src/api/createMealtimeCategories/createMealtimeCategories';
import { IAssignCategoriesView } from '../types';
import styles from './AssignCategoriesView.module.scss';
import EmptyState from 'src/components/EmptyState';
import { useMealtimes } from 'src/hooks/useMealtimes/useMealtimes';
import { CheckboxList } from '../../CheckboxList/CheckboxList';
import { getCategoryItemCount } from '../../../helpers/getCategoryItemCount/getCategoryItemCount';

const AssignCategoriesView = ({
  categoriesData,
  closeModal,
  mealtimeId,
  menuId,
  preselectedCategories,
}: IAssignCategoriesView) => {
  const { showSnackbar } = useSnackbar();
  const location = useLocation();
  const navigate = useNavigate();
  const noCategoryText =
    "Before you can use mealtimes, you'll need to create at least one menu category. When you create a category, you can assign mealtimes to it directly. Alternatively, you can assign a category to a mealtime, when you create or edit it.";
  const parentCategories = categoriesData?.filter((category) => !category.parent_section_id);

  const getInitialValues = (): Record<string, boolean> =>
    parentCategories?.reduce<Record<string, boolean>>(
      (acc, data) => ({
        ...acc,
        [data.section_id]: preselectedCategories?.find((cate) => cate.category_id === data.section_id) ? true : false,
      }),
      {},
    );

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { isDirty },
  } = useForm<Record<string, boolean>>({
    defaultValues: getInitialValues(),
    mode: 'onChange',
  });

  const handleBackToMenu = () => {
    navigate(`/menus/${menuId}`);
  };

  const { refetch } = useMealtimes(menuId);

  const handleCloseModal = () => {
    closeModal();
    if (location.pathname.includes(mealtimeId)) {
      refetch();
    } else {
      navigate(`${location.pathname}/${mealtimeId}`);
    }
  };

  const onSubmit = async (data: Record<string, boolean>) => {
    const selectedCategories: string[] = Object.keys(data).filter((categoryId) => data[categoryId]);

    await createMealtimeCategories(menuId, mealtimeId, selectedCategories)
      .then(() => {
        handleCloseModal();
      })
      .catch(() => {
        showSnackbar({
          status: SnackbarStatus.ERROR,
          message: 'Something went wrong whilst assigning categories to a mealtime',
        });
      });
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Typography size="xxlarge" weight="semi-bold">
        {preselectedCategories ? `Edit Categories` : `Assign Category to Mealtime`}
      </Typography>
      {parentCategories.length > 0 ? (
        <CheckboxList
          watch={watch}
          setValue={setValue}
          register={register}
          checkboxes={parentCategories.map((category) => ({
            id: category.section_id,
            label: category.name,
            description: `${getCategoryItemCount(category, categoriesData)} items`,
          }))}
        />
      ) : (
        <div className={styles['no-categories']}>
          <EmptyState
            Icon={() => <SaladIcon size="large" />}
            emptySectionText={noCategoryText}
            buttonText="Go To Menu"
            onClick={handleBackToMenu}
          />
        </div>
      )}
      <div className={styles['buttons-container']}>
        <SecondaryButton size="full-width" buttonStyle="outline" type="button" onClick={handleCloseModal}>
          {preselectedCategories?.length > 0 ? 'Back' : 'Skip'}
        </SecondaryButton>
        <PrimaryButton disabled={!isDirty} size="full-width" type="submit">
          Save
        </PrimaryButton>
      </div>
    </form>
  );
};

export default AssignCategoriesView;
